import './index.js';

import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {ImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {AbstractConstructor} from '@gl/lit/types/classes.js';
import {fetcher} from '@gl/lit/utils/http.js';
import {RequestMethod} from '@gl/lit/common/constants/index.js';
import {isPlainObject} from '@gl/lit/utils/index.js';
import {when} from 'lit/directives/when.js';
import {choose} from 'lit/directives/choose.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import exampleStyles from './style.scss';
import type {Model} from './architecture.js';
import {FetchState, Msg, main} from './architecture.js';
import {either} from 'fp-ts';
import type {Either} from 'fp-ts/Either';
import {GREETINGS, PLANETS, pickAtRandom} from './constants.js';
import {type Greeting, type Planet} from './types.js';

abstract class ReactiveParent extends FlywheelComponent {
  @property({type: Number})
  count = 2;

  increment() {
    this.count += 1;
  }

  decrement() {
    this.count -= 1;
  }

  renderCounter() {
    return html`
      <div>
        <button @click=${this.decrement.bind(this)}>-</button>
        ${this.count}
        <button @click=${this.increment.bind(this)}>+</button>
      </div>
    `;
  }
}

@customElement('immutable-planet-greeter')
export default class ExampleGreeter extends ImmutableComponent<
  AbstractConstructor<ReactiveParent>,
  Model,
  Msg
>({component: ReactiveParent, main}) {
  @property() greeting = 'hello000';
  @property() planet = 'world';

  static get styles() {
    return [exampleStyles];
  }

  async fetchIt(): Promise<Either<Error, Record<string, unknown>>> {
    const url = '/api/v2/teams';

    try {
      const article = await fetcher(url, {method: RequestMethod.GET});
      if (!isPlainObject(article)) throw Error('Invald response type');
      return either.right(article);
    } catch (err) {
      console.error(err);
      return either.left(Error('Failed to fetch it!'));
    }
  }

  randomGreeting(): Greeting {
    return pickAtRandom([...GREETINGS]) as Greeting;
  }

  randomPlanet(): Planet {
    return pickAtRandom([...PLANETS]) as Planet;
  }

  render() {
    return html`
      <h1>${this.model.greeting} ${this.model.planet}!</h1>
      ${this.model.count}
      ${this.renderCounter()}
      <div>
        <button @click=${() =>
          this.handleUpdate(Msg.setGreeting(this.randomGreeting()))}>
          New Greeting
        </button>
        <button @click=${() =>
          this.handleUpdate(Msg.setPlanet(this.randomPlanet()))}>
          New Planet
        </button>
        <button @click=${() =>
          this.handleUpdate(Msg.randomize())}>Randomize me</button>
        <button @click=${() =>
          this.handleUpdate(Msg.fetchArticle())}>Fetch!</button>
        <button @click=${() => this.handleUpdate(Msg.setCount(3))}>
          Set count to 3.
        </button>
      </div>
      </div>
        <button @click=${() => this.stepBack()}>Step back</button>
        <button @click=${() => this.stepForward()}>Step forward</button>
      </div>
      <p>
        <strong>Channel messages test:</strong> received ${
          this.model.messagesReceived
        }
      </p>
      <p>
        <strong>Current version:</strong>: ${this.currentVersion()}
      </p>
      <p>
        ${choose(
          this.model.fetchState,
          [
            [FetchState.INITIAL, () => html`Click above to fetch article.`],
            [FetchState.LOADING, () => html`Fetching article`],
            [FetchState.ERR, () => html`Error fetching article!`],
            [
              FetchState.SUCCESS,
              () => html`
                ${when(
                  this.model.article,
                  () => html`${JSON.stringify(this.model.article!)}`,
                  () => html`Unexpcted error.`
                )}
              `,
            ],
          ],
          () => html`UNKNOWN ERROR`
        )}
      </p>
    `;
  }
}
